import type { BasicQuestion } from "@repo/app-types/client/client";
import type { Company } from "@repo/app-types/entities/company";
import type { Project, ProjectUpdate } from "@repo/app-types/entities/project";
import { ProjectMode } from "@repo/app-types/entities/project";
import type {
  ComplexQuestion,
  ComplexQuestionInsert,
} from "@repo/app-types/entities/question";
import {
  isComplexProjectQuestion,
  isComplexProjectQuestionInsert,
  isValidComplexQuestion,
  reorderArrayItems,
} from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { Slideover } from "@repo/ui/Slideover/slideover";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { InfoIconTooltip } from "@repo/ui/tooltip/infoIconTooltip";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import isEqual from "lodash-es/isEqual.js";
import { type FC, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { AddSurveyQuestionsDropdown } from "./addSurveyQuestionsDropdown.js";
import { BasicQuestionEditor } from "./basicQuestionEditor.js";
import { ComplexQuestionEditor } from "./complexQuestionEditor.js";
import { ImportQuestionsModal } from "./importQuestionsModal.js";
import { QuestionsManagerCell } from "./questionsManagerCell.js";

const MAX_INTERVIEW_QUESTIONS = 25;

interface QuestionsManagerSlideoverProps {
  complexQuestions: (ComplexQuestion | ComplexQuestionInsert)[];
  basicQuestions: BasicQuestion[];
  project: Project;
  company: Company;
  onSaveProject: (changes: ProjectUpdate) => void;
  isOpen: boolean;
  onClose: () => void;
}

const emptyBasicQuestion = { question: "" };

export const QuestionsManagerSlideover: FC<QuestionsManagerSlideoverProps> = (
  props,
) => {
  const { project, onSaveProject, isOpen, company } = props;

  // The question that's currently selected (can be survey question or interview question)
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [complexQuestions, setComplexQuestions] = useState<
    (ComplexQuestion | ComplexQuestionInsert)[]
  >(props.complexQuestions);
  const [basicQuestions, setBasicQuestions] = useState<BasicQuestion[]>(
    props.basicQuestions.length > 0
      ? props.basicQuestions
      : [emptyBasicQuestion],
  );
  const [hasMadeChanges, setHasMadeChanges] = useState(false);
  const [isSaveConfirmationModalOpen, setIsSaveConfirmationModalOpen] =
    useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const questionsContainerRef = useRef<HTMLDivElement>(null);

  // Since complex questions are shown first, we need to subtract the number of complex questions
  const basicQuestionIndex = selectedIndex - complexQuestions.length;
  const currentQuestion =
    selectedIndex < complexQuestions.length
      ? complexQuestions[selectedIndex]
      : basicQuestions[basicQuestionIndex];

  useEffect(() => {
    // When the slideover opens and closes, make sure we update our state with the latest questions
    setComplexQuestions(props.complexQuestions);
    setBasicQuestions(
      props.basicQuestions.length > 0
        ? props.basicQuestions
        : [emptyBasicQuestion],
    );
  }, [isOpen]);

  const onSave = () => {
    // For each complexQuestion that was edited, remove the ID so that the server treats it as a new question
    const updatedComplexQuestions = complexQuestions.map((q) => {
      const matchingOriginalQuestion = props.complexQuestions.find(
        (originalQuestion) =>
          "id" in q && "id" in originalQuestion && q.id === originalQuestion.id,
      );
      if (!isEqual(q, matchingOriginalQuestion)) {
        return {
          ...q,
          id: undefined,
        };
      }

      return q;
    });

    onSaveProject({
      questions: [
        ...updatedComplexQuestions,
        ...basicQuestions.filter((q) => q.question), // Filter out an empty basic questions
      ],
    });

    onClose();
  };

  const onClose = () => {
    // Reset questions to currently saved state
    setComplexQuestions(props.complexQuestions);
    setBasicQuestions(props.basicQuestions);
    setHasMadeChanges(false);
    setSelectedIndex(0);
    props.onClose();
  };

  const onClickReorderBasicQuestion = (
    index: number,
    direction: "up" | "down",
  ) => {
    // Reorder the index'th element in the questions array
    const newQuestions = reorderArrayItems(basicQuestions, index, direction);

    // If the reorder is invalid, don't update.
    if (!newQuestions) return;

    setBasicQuestions(newQuestions);
    setHasMadeChanges(true);
  };

  const onClickReorderComplexQuestion = (
    index: number,
    direction: "up" | "down",
  ) => {
    // Reorder the index'th element in the questions array
    const newQuestions = reorderArrayItems(complexQuestions, index, direction);

    // If the reorder is invalid, don't update.
    if (!newQuestions) return;

    setComplexQuestions(newQuestions);
    setHasMadeChanges(true);
  };

  const renderCurrentQuestionContent = () => {
    if (!currentQuestion) return null;

    if (
      isComplexProjectQuestion(currentQuestion) ||
      isComplexProjectQuestionInsert(currentQuestion)
    ) {
      return (
        <ComplexQuestionEditor
          question={currentQuestion}
          onChange={(question) => {
            // Replace question at the currentIndex
            setComplexQuestions(
              complexQuestions.map((q, i) =>
                i === selectedIndex ? question : q,
              ),
            );
            setHasMadeChanges(true);
          }}
        />
      );
    }

    return (
      <BasicQuestionEditor
        question={currentQuestion}
        onChange={(question) => {
          setBasicQuestions(
            basicQuestions.map((q, i) =>
              i === basicQuestionIndex ? question : q,
            ),
          );
          setHasMadeChanges(true);
        }}
        key={`basic-question-${basicQuestionIndex}`}
      />
    );
  };

  const noInterviewQuestionsOnLiveProject =
    basicQuestions.length === 0 && project.is_live;
  const isAtInterviewQuestionLimit =
    basicQuestions.length >= MAX_INTERVIEW_QUESTIONS;

  const addInterviewQuestionButton = (
    <Button
      onClick={() => {
        setBasicQuestions((previousBasicQuestions) => {
          const newQuestions = [...previousBasicQuestions, emptyBasicQuestion];
          // Update basicQuestions and immediately calculate and set selectedIndex
          setSelectedIndex(complexQuestions.length + newQuestions.length - 1);

          setTimeout(() => {
            if (questionsContainerRef.current) {
              questionsContainerRef.current.scrollTop =
                questionsContainerRef.current.scrollHeight;
            }
          }, 100);

          return newQuestions;
        });
      }}
      variant={ButtonVariant.Secondary}
      size={Size.SMALL}
      isDisabled={isAtInterviewQuestionLimit}
    >
      Add
    </Button>
  );

  const saveButton = (
    <Button
      variant={ButtonVariant.Primary}
      onClick={() => {
        // We don't check for basicQuestion errors because we automatically filter out empty basic questions when saving
        const hasError = Boolean(
          complexQuestions.find((q) => !isValidComplexQuestion(q, true)),
        );
        if (hasError) {
          showToast({
            title:
              "Some of your questions have errors - please fix them and try again.",
            variant: ToastVariant.Error,
          });
          return;
        }

        if (project.is_live) setIsSaveConfirmationModalOpen(true);
        else onSave();
      }}
      size={Size.MEDIUM}
      isDisabled={!hasMadeChanges || noInterviewQuestionsOnLiveProject}
    >
      Save changes
    </Button>
  );

  return (
    <>
      <Slideover
        title="Edit interview questions"
        onClose={onClose}
        shouldShow={isOpen}
        buttons={
          <>
            {noInterviewQuestionsOnLiveProject ? (
              // A disabled button fires no hover events, so the tooltip hangs off a wrapper.
              <TooltipTrigger>
                <TooltipHoverTarget shape="rounded">
                  {saveButton}
                </TooltipHoverTarget>
                <SimpleTooltip>
                  At least one interview question is required on a live project.
                  If you&apos;d like to stop interviews for this project, you
                  can pause it instead.
                </SimpleTooltip>
              </TooltipTrigger>
            ) : (
              saveButton
            )}
            <Button
              variant={ButtonVariant.Secondary}
              onClick={onClose}
              size={Size.MEDIUM}
            >
              Discard changes
            </Button>
          </>
        }
      >
        <div className="flex h-full flex-row divide-y divide-gray-100 bg-white text-sm leading-6">
          <div
            className="w-[400px] shrink-0 overflow-y-auto border-r border-border-light"
            ref={questionsContainerRef}
          >
            {project.mode === ProjectMode.SURVEY ? (
              <>
                <div className="space-between flex flex-row items-center justify-between px-3 py-2">
                  <div className="flex flex-row items-center space-x-2">
                    <Label size={Size.SMALL}>Survey questions</Label>
                    <InfoIconTooltip>
                      Use survey questions to collect structured, quantitative
                      data before the open-ended interview questions.
                    </InfoIconTooltip>
                  </div>

                  <AddSurveyQuestionsDropdown
                    company={company}
                    project={project}
                    onAddQuestion={(newQuestion) => {
                      setComplexQuestions([...complexQuestions, newQuestion]);
                      setSelectedIndex(complexQuestions.length);
                      setHasMadeChanges(true);
                    }}
                    complexQuestions={complexQuestions}
                  />
                </div>
                {complexQuestions.map((question, index) => {
                  return (
                    <QuestionsManagerCell
                      key={`complex-question-${index}`}
                      questionNumber={index + 1}
                      question={question}
                      isSelected={selectedIndex === index}
                      onClick={() => {
                        setSelectedIndex(index);
                      }}
                      onRemove={() => {
                        setComplexQuestions(
                          complexQuestions.filter((_, i) => i !== index),
                        );
                        setHasMadeChanges(true);
                      }}
                      onClickMoveUp={
                        index !== 0
                          ? () => {
                              onClickReorderComplexQuestion(index, "up");
                            }
                          : undefined
                      }
                      onClickMoveDown={
                        index !== complexQuestions.length - 1
                          ? () => {
                              onClickReorderComplexQuestion(index, "down");
                            }
                          : undefined
                      }
                    />
                  );
                })}
              </>
            ) : null}
            <div
              className={twMerge(
                "space-between flex flex-row items-center justify-between px-3 py-2",
                project.mode === ProjectMode.SURVEY ? "mt-4" : "mt-0",
              )}
            >
              <div className="flex flex-row items-center space-x-2">
                <Label size={Size.SMALL}>AI interview questions</Label>
                <InfoIconTooltip>
                  Participants answer these questions out loud one at a time and
                  we&apos;ll automatically ask clarifying questions based on
                  their answers. For the best insights, we recommend simple,
                  open-ended questions.
                </InfoIconTooltip>
              </div>
              <div className="flex space-x-2">
                <Button
                  onClick={() => {
                    setIsImportModalOpen(true);
                  }}
                  variant={ButtonVariant.Secondary}
                  size={Size.SMALL}
                >
                  Import
                </Button>
                {isAtInterviewQuestionLimit ? (
                  // A disabled button fires no hover events, so the tooltip hangs off a wrapper.
                  <TooltipTrigger>
                    <TooltipHoverTarget shape="rounded">
                      {addInterviewQuestionButton}
                    </TooltipHoverTarget>
                    <SimpleTooltip>{`To ensure a good interview experience, we support up to ${MAX_INTERVIEW_QUESTIONS} interview questions.`}</SimpleTooltip>
                  </TooltipTrigger>
                ) : (
                  addInterviewQuestionButton
                )}
              </div>
            </div>
            {basicQuestions.map((question, index) => {
              const questionNumber = complexQuestions.length + index + 1;
              return (
                <QuestionsManagerCell
                  key={`basic-question-${index}`}
                  questionNumber={questionNumber}
                  question={question}
                  isSelected={selectedIndex === complexQuestions.length + index}
                  onClick={() => {
                    setSelectedIndex(complexQuestions.length + index);
                  }}
                  onRemove={() => {
                    setHasMadeChanges(true);
                    setBasicQuestions(
                      basicQuestions.filter((_, i) => i !== index),
                    );
                  }}
                  onClickMoveUp={
                    index !== 0
                      ? () => {
                          onClickReorderBasicQuestion(index, "up");
                        }
                      : undefined
                  }
                  onClickMoveDown={
                    index !== basicQuestions.length - 1
                      ? () => {
                          onClickReorderBasicQuestion(index, "down");
                        }
                      : undefined
                  }
                />
              );
            })}
          </div>
          <div className="w-full overflow-y-auto bg-background px-4 py-3">
            {renderCurrentQuestionContent()}
          </div>
        </div>
        <SimpleModal
          isOpen={isSaveConfirmationModalOpen}
          variant={SimpleModalVariant.Standard}
          title="Update interview questions?"
          subtitle="Existing interview transcripts will remain unchanged, but future interviews and insights will use latest version of your questions."
          confirmButtonText="Proceed"
          onCancel={() => {
            setIsSaveConfirmationModalOpen(false);
          }}
          onConfirm={onSave}
        />
        <ImportQuestionsModal
          isOpen={isImportModalOpen}
          onClose={() => {
            setIsImportModalOpen(false);
          }}
          onImport={(questions: BasicQuestion[]) => {
            setBasicQuestions(questions);
            setHasMadeChanges(true);
            setIsImportModalOpen(false);
          }}
        />
      </Slideover>
    </>
  );
};
